import {
  Badge,
  Button,
  Empty,
  IcnAlert,
  IcnMail,
  IcnRefresh,
  StatusBar,
} from '@viquard/doc-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/datetime';
import { useDigestPreview } from '../api.js';
import { notificationCopy } from '../format.js';

export function NotificationDigestPreview(): ReactNode {
  const { t, i18n } = useTranslation('settings');
  const digestPreview = useDigestPreview();

  return (
    <section
      className="flex flex-col gap-3"
      style={{
        padding: 'var(--vq-space-5)',
        background: 'var(--vq-paper-raised)',
        border: '1px solid var(--vq-rule)',
        borderRadius: 'var(--vq-radius-lg)',
      }}
      aria-label={t('notifications.digestPreview.title')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="m-0 flex items-center gap-2 text-base font-semibold">
            <IcnMail size={16} /> {t('notifications.digestPreview.title')}
          </h3>
          <p className="m-0 mt-1 text-sm text-[color:var(--vq-text-muted)]">
            {t('notifications.digestPreview.subtitle')}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          iconLeft={<IcnRefresh size={14} />}
          loading={digestPreview.isFetching}
          onClick={() => {
            void digestPreview.refetch();
          }}
        >
          {t('notifications.refresh')}
        </Button>
      </div>

      {digestPreview.isPending ? (
        <StatusBar
          left={<StatusBar.Item tone="neutral">{t('notifications.digestPreview.loading')}</StatusBar.Item>}
        />
      ) : digestPreview.error ? (
        <Empty
          icon={<IcnAlert size={18} />}
          title={t('notifications.digestPreview.loadFailed')}
        />
      ) : digestPreview.data.items.length === 0 ? (
        <Empty
          icon={<IcnMail size={18} />}
          title={t('notifications.digestPreview.empty')}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <Badge tone="info" variant="soft" size="sm">
            {t('notifications.digestPreview.generatedAt', {
              date: formatDateTime(digestPreview.data.generatedAt, i18n.language),
            })}
          </Badge>
          {digestPreview.data.items.slice(0, 6).map((item) => {
            const title = notificationCopy(item.emailSubject, i18n);
            const body = notificationCopy(item.emailPreview, i18n);
            return (
              <article
                key={item.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-[color:var(--vq-rule)] px-3 py-2"
              >
                <div className="min-w-0">
                  <h4 className="m-0 text-sm font-semibold">{title}</h4>
                  <p className="m-0 mt-1 text-sm text-[color:var(--vq-text-muted)]">
                    {body}
                  </p>
                  <p className="m-0 mt-1 text-xs text-[color:var(--vq-ink-3)]">
                    {item.actionLabel}
                  </p>
                </div>
                <span className="flex flex-wrap justify-end gap-2">
                  <Badge tone="info" variant="soft" size="sm">
                    {t(`notifications.digestPreview.cadence.${item.cadence}`, {
                      defaultValue: item.cadence,
                    })}
                  </Badge>
                  <Badge tone="neutral" variant="soft" size="sm">
                    {formatDateTime(item.scheduledFor, i18n.language)}
                  </Badge>
                </span>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
